import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api, { formatINR } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';

export default function SellerProducts() {
  const { seller } = useOutletContext();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get('/sellers/me/products'),
        api.get('/categories'),
      ]);
      setProducts(p.data || []);
      setCategories(c.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (seller?.status === 'approved') load(); }, [seller]);

  const openCreate = () => { setEditing(null); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setShowForm(true); };
  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/sellers/me/products/${p.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  if (seller?.status !== 'approved') {
    return <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center text-stone-600">Your store is awaiting approval. You can add products once approved.</div>;
  }

  return (
    <div className="space-y-5" data-testid="seller-products">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-3xl text-stone-900">Products</h1><p className="text-sm text-stone-500">{products.length} total</p></div>
        <button data-testid="seller-add-product-btn" onClick={openCreate} className="h-10 px-4 rounded-full bg-terracotta-600 text-white text-sm font-semibold hover:bg-terracotta-700 flex items-center gap-1"><Plus size={16}/> Add product</button>
      </div>

      {loading ? <p className="text-stone-500">Loading…</p> : products.length === 0 ? (
        <div className="bg-white border border-dashed border-stone-200 rounded-2xl p-10 text-center text-stone-500">No products yet. Add your first one.</div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream-100 text-stone-600 text-xs uppercase tracking-wider">
              <tr><th className="text-left p-3">Product</th><th>Price</th><th>Stock</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-stone-100" data-testid={`seller-product-row-${p.id}`}>
                  <td className="p-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cream-100 overflow-hidden grid place-items-center">
                      {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover"/> : <span className="text-xs text-stone-400">—</span>}
                    </div>
                    <div><p className="font-medium text-stone-900">{p.name}</p><p className="text-xs text-stone-500">{p.unit}</p></div>
                  </td>
                  <td className="text-center">{formatINR(p.price)}</td>
                  <td className="text-center">{p.stock}</td>
                  <td className="text-center"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${p.is_active ? 'bg-olive-100 text-olive-800' : 'bg-stone-100 text-stone-600'}`}>{p.is_active ? 'ACTIVE' : 'HIDDEN'}</span></td>
                  <td className="text-right pr-3">
                    <button onClick={() => openEdit(p)} className="w-8 h-8 rounded-full hover:bg-stone-100 grid place-items-center" title="Edit"><PencilSimple size={14}/></button>
                    <button onClick={() => del(p)} className="w-8 h-8 rounded-full hover:bg-rose-50 text-rose-600 grid place-items-center" title="Delete"><Trash size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <ProductForm
          product={editing}
          categories={categories}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function ProductForm({ product, categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: product?.name || '',
    description: product?.description || '',
    category_id: product?.category_id || categories[0]?.id || '',
    price: product?.price || 0,
    mrp: product?.mrp || 0,
    unit: product?.unit || '',
    image_url: product?.image_url || '',
    is_active: product?.is_active !== false,
    stock_status: product?.stock_status || 'in_stock',
    stock: product?.stock ?? 0,
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (product) await api.put(`/sellers/me/products/${product.id}`, { ...form, price: Number(form.price), mrp: Number(form.mrp), stock: Number(form.stock) });
      else await api.post('/sellers/me/products', { ...form, price: Number(form.price), mrp: Number(form.mrp), stock: Number(form.stock) });
      toast.success(product ? 'Updated' : 'Added');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-2xl w-full max-w-xl p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="seller-product-form">
        <h3 className="font-heading text-xl text-stone-900">{product ? 'Edit product' : 'Add product'}</h3>
        <F label="Name"><input required value={form.name} onChange={(e) => upd('name', e.target.value)} className="inp" data-testid="prod-name"/></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Category">
            <select value={form.category_id} onChange={(e) => upd('category_id', e.target.value)} className="inp" data-testid="prod-category">
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </F>
          <F label="Unit (e.g. 500g)"><input value={form.unit} onChange={(e) => upd('unit', e.target.value)} className="inp" data-testid="prod-unit"/></F>
          <F label="Price (₹)"><input type="number" min="0" step="0.01" value={form.price} onChange={(e) => upd('price', e.target.value)} className="inp" data-testid="prod-price"/></F>
          <F label="MRP (₹)"><input type="number" min="0" step="0.01" value={form.mrp} onChange={(e) => upd('mrp', e.target.value)} className="inp" data-testid="prod-mrp"/></F>
          <F label="Stock qty"><input type="number" min="0" value={form.stock} onChange={(e) => upd('stock', e.target.value)} className="inp" data-testid="prod-stock"/></F>
          <F label="Stock status">
            <select value={form.stock_status} onChange={(e) => upd('stock_status', e.target.value)} className="inp">
              <option value="in_stock">In stock</option>
              <option value="out_of_stock">Out of stock</option>
            </select>
          </F>
        </div>
        <F label="Image URL (optional)"><input value={form.image_url} onChange={(e) => upd('image_url', e.target.value)} className="inp" placeholder="https://…"/></F>
        <F label="Description"><textarea rows="3" value={form.description} onChange={(e) => upd('description', e.target.value)} className="inp"/></F>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => upd('is_active', e.target.checked)}/> Active (visible on store)</label>
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-full border border-stone-300 text-sm">Cancel</button>
          <button type="submit" disabled={busy} data-testid="prod-save-btn" className="h-10 px-5 rounded-full bg-terracotta-600 text-white text-sm font-semibold hover:bg-terracotta-700 disabled:bg-stone-300">{busy ? 'Saving…' : 'Save'}</button>
        </div>
        <style>{`.inp{width:100%;height:40px;padding:0 12px;border:1px solid #e7e5e4;border-radius:10px;font-size:14px;background:white;}textarea.inp{height:auto;padding:10px 12px;}`}</style>
      </form>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="text-xs font-medium text-stone-600 block mb-1">{label}</label>{children}</div>;
}
